import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, setAuth, errMsg } from "../lib/api";
import { startGoogleSignIn } from "../lib/googleAuth";
import { BrandLogo } from "../components/BrandLogo";
import { ArrowRight, Mail, Lock, Sparkles } from "lucide-react";

export default function Login() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setAuth(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name}`);
      nav(data.user.role === "admin" ? "/admin" : "/seller");
    } catch (err) {
      toast.error(errMsg(err, "Invalid credentials"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-[#030712] text-white relative overflow-hidden">
      {/* Ambient gradients */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(900px 700px at 0% 0%, rgba(79,70,229,0.20), transparent 60%), radial-gradient(900px 700px at 100% 100%, rgba(124,58,237,0.18), transparent 65%), radial-gradient(600px 500px at 50% 100%, rgba(6,182,212,0.08), transparent 70%)",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-50 mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E\")",
        }}
      />

      {/* Left: brand + value prop */}
      <div className="relative z-10 hidden md:flex flex-col justify-between p-12">
        <Link to="/" className="inline-block">
          <BrandLogo size={44} showWordmark />
        </Link>

        <div className="max-w-md">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-[10px] uppercase tracking-[0.25em] text-indigo-300 font-bold mb-5">
            <Sparkles size={11} /> AI Growth Engine
          </div>
          <h2
            className="font-display text-5xl font-semibold tracking-tighter leading-[1.05]"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Turn raw sales data into a{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #818cf8 0%, #c084fc 50%, #f472b6 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              growth plan.
            </span>
          </h2>
          <p className="text-slate-400 mt-5 text-base leading-relaxed">
            Admin-controlled access. Multi-tenant. Claude-powered insights. 6-sheet AI Excel
            delivered to your inbox.
          </p>

          {/* Decorative metric tiles */}
          <div className="mt-10 grid grid-cols-3 gap-3 max-w-sm">
            {[
              { v: "90s", l: "AI listing" },
              { v: "7", l: "Marketplaces" },
              { v: "225+", l: "Categories" },
            ].map((x) => (
              <div
                key={x.l}
                className="p-3 rounded-2xl bg-white/[0.025] border border-white/[0.08] backdrop-blur-md"
              >
                <div
                  className="font-semibold text-2xl tracking-tight"
                  style={{
                    background: "linear-gradient(135deg, #f8fafc 0%, #a5b4fc 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontFamily: "'Outfit', sans-serif",
                  }}
                >
                  {x.v}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">
                  {x.l}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-slate-500" data-testid="login-left-attribution">
          Made by EcomCatalog AI — All Rights Reserved.
        </div>
      </div>

      {/* Right: glass card form */}
      <div className="relative z-10 flex flex-col justify-center px-6 md:px-16 py-10">
        <div className="md:hidden mb-8">
          <BrandLogo size={40} showWordmark />
        </div>
        <div
          className="max-w-md w-full mx-auto p-8 sm:p-10 rounded-3xl border border-white/[0.1] backdrop-blur-2xl"
          style={{
            background: "rgba(255,255,255,0.025)",
            boxShadow: "0 30px 80px -20px rgba(79,70,229,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
          }}
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-300 font-bold">
            Seller / Admin Login
          </div>
          <h1
            className="text-3xl sm:text-4xl font-semibold tracking-tighter mt-2 text-white"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Welcome back.
          </h1>
          <p className="text-sm text-slate-400 mt-2">
            Use the credentials issued by your EcomCatalog AI admin.
          </p>

          <form onSubmit={onSubmit} className="mt-7 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                Email
              </label>
              <div className="mt-1.5 relative">
                <Mail size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-3 py-3 bg-white/[0.03] border border-white/[0.1] rounded-xl text-white placeholder:text-slate-600 focus:outline-none focus:border-indigo-400/60 focus:ring-1 focus:ring-indigo-400/30 transition-all"
                  placeholder="you@brand.com"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                Password
              </label>
              <div className="mt-1.5 relative">
                <Lock size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-3 py-3 bg-white/[0.03] border border-white/[0.1] rounded-xl text-white placeholder:text-slate-600 focus:outline-none focus:border-indigo-400/60 focus:ring-1 focus:ring-indigo-400/30 transition-all"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-3 inline-flex items-center justify-center gap-2 py-3 rounded-xl text-white font-semibold disabled:opacity-60 transition-all"
              style={{
                background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                boxShadow: "0 14px 30px -10px rgba(79,70,229,0.6), inset 0 1px 0 rgba(255,255,255,0.15)",
              }}
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in…" : (<>Sign In <ArrowRight size={14} /></>)}
            </button>
          </form>

          {/* Custom Google OAuth (own GCP client — no third-party branding).
              REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR
              REDIRECT URLS, THIS BREAKS THE AUTH. */}
          <div className="mt-5">
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              <span className="flex-1 h-px bg-white/10" /> or <span className="flex-1 h-px bg-white/10" />
            </div>
            <button
              type="button"
              onClick={() => startGoogleSignIn()}
              className="mt-4 w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-white text-[#1F1F1F] text-sm font-bold hover:bg-slate-100 transition-colors"
              data-testid="google-signin-btn"
            >
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.4 35.3 44 30 44 24c0-1.3-.1-2.6-.4-3.9z"/>
              </svg>
              Continue with Google
            </button>
          </div>

          <div className="mt-6 text-center text-xs text-slate-500">
            Don't have an account?{" "}
            <Link to="/signup" className="text-indigo-300 hover:text-indigo-200 font-medium" data-testid="login-signup-link">
              Start free trial — 1 listing free
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
